import { FieldInput } from "./FieldInput";
import { PIPE_SIZES, RETENTION_TANK_OPTIONS } from "../../lib/defaults";
import { Info } from "lucide-react";

export default function StepSystem({ state, set }) {
  return (
    <section data-testid="step-system" className="space-y-8">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
          System<br/>Parameters
        </h1>
        <p className="text-white/70 text-base mt-4">
          These dictate <span className="text-[#F26522] font-bold">how much</span> chlorine you need and <span className="text-[#F26522] font-bold">how fast</span> it reacts.
        </p>
      </div>

      {/* Master variables — pH + Temp are now in Step 01 (Client & Water Quality) */}

      {/* Plumbing */}
      <div className="space-y-5">
        <div>
          <div className="font-heading text-xl font-black uppercase text-white mb-1">Plumbing & Flow</div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#F26522]">Meter & Pump Sizing</div>
        </div>

        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/70 font-bold block mb-2">
            Service Pipe Diameter
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PIPE_SIZES.map(s => (
              <button
                key={s}
                type="button"
                data-testid={`pipe-${s}`}
                onClick={() => set("pipe_size_in", s)}
                className={`h-16 border-2 font-mono font-black text-lg transition-colors ${
                  Number(state.pipe_size_in) === s
                    ? "border-[#F26522] bg-[#F26522] text-black"
                    : "border-white/15 bg-[#1A1A1A] text-white hover:border-white/40"
                }`}
              >
                {s === 0.75 ? "3/4″" : s === 1.5 ? "1-1/2″" : `${s}″`}
              </button>
            ))}
          </div>
        </div>

        <FieldInput
          id="flow_rate_gpm"
          testId="input-flow-rate"
          label="Peak Flow Rate"
          unit="GPM"
          value={state.flow_rate_gpm}
          onChange={v => set("flow_rate_gpm", v)}
          placeholder="10"
          step="0.5"
        />
        <FieldInput
          id="daily_usage_gallons"
          testId="input-daily-usage"
          label="Daily Water Usage"
          unit="gal/day"
          value={state.daily_usage_gallons}
          onChange={v => set("daily_usage_gallons", v)}
          placeholder="300"
          step="10"
          help="Rule of thumb: ~80 gal/day per person."
        />
      </div>

      {/* CONTACT TIME selector */}
      <div className="space-y-5">
        <div>
          <div className="font-heading text-xl font-black uppercase text-white mb-1">Contact Time</div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#F26522]">Tank Selection</div>
        </div>

        <div className="space-y-3">
          {RETENTION_TANK_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              data-testid={`rt-${opt.value}`}
              onClick={() => set("retention_tank_type", opt.value)}
              className={`w-full text-left p-5 border-2 transition-colors flex items-start gap-4 ${
                state.retention_tank_type === opt.value
                  ? "border-[#F26522] bg-black"
                  : "border-white/15 bg-[#1A1A1A] hover:border-white/40"
              }`}
            >
              <div className={`w-12 h-12 shrink-0 flex flex-col items-center justify-center font-mono font-black ${
                state.retention_tank_type === opt.value ? "bg-[#F26522] text-black" : "bg-white/10 text-white/60"
              }`}>
                {opt.value === "none" ? (
                  <div className="text-[8px] leading-tight text-center px-1">NO<br/>TANK</div>
                ) : opt.value === "custom" ? (
                  <div className="text-2xl leading-none">+</div>
                ) : (
                  <>
                    <div className="text-xs leading-none">{opt.gallons}</div>
                    <div className="text-[8px] leading-none mt-0.5">GAL</div>
                  </>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-black uppercase text-base text-white">{opt.label}</div>
                <div className="text-xs text-white/60 mt-1">{opt.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {state.retention_tank_type === "custom" && (
          <FieldInput
            id="retention_tank_custom_gallons"
            testId="input-rt-custom"
            label="Holding Tank Size"
            unit="gallons"
            value={state.retention_tank_custom_gallons}
            onChange={v => set("retention_tank_custom_gallons", v)}
            placeholder="120"
            step="10"
            min="0"
          />
        )}

        {(state.retention_tank_type === "RT40" || state.retention_tank_type === "none") && (
          <div className="bg-[#1A1A1A] border-l-4 border-[#F26522] p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-[#F26522] shrink-0 mt-0.5" />
              <div className="text-sm text-white/80">
                <strong className="text-white">Dose Boost Mode</strong> is enabled by default. When contact time is short,
                ProDose will <span className="text-[#F26522] font-bold">increase the chlorine dose</span> instead of forcing a tank upgrade.
                <button
                  type="button"
                  data-testid="toggle-rt40-boost"
                  onClick={() => set("allow_rt40_dose_boost", !state.allow_rt40_dose_boost)}
                  className="block mt-3 font-mono text-[11px] uppercase tracking-wider text-[#F26522] hover:underline"
                >
                  {state.allow_rt40_dose_boost ? "✓ Boost mode ON — click to disable" : "✗ Boost mode OFF — click to enable"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </section>
  );
}
